import React from "react";
import { useHistory } from "react-router-dom";
import {
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Spinner,
} from "reactstrap";
import Autosuggest from "react-autosuggest";
import PropTypes from "prop-types";

// app
import {
  AddressNotFoundMessage,
  AddressSearchErrorMessage,
  AddressSuggestionsPortal,
  PropertyIcon,
  renderAddressSuggestion,
  useAddressAutosuggest,
} from "../../components/AddressAutosuggest";
import { SUPPORT_EMAIL } from "../../constants";
import { nationalIntakeEntryUrl } from "../../lib/nationalIntakeRoute";
import {
  SmallSpinner,
  CountyNote,
  AddressPillWrap,
} from "./styled";
import "./autosuggest-theme.css";

const CURRENT_STEP = 0;

const LookupAddressForm = ({
  addressSuggestions,
  suggestionsFailed,
  onSubmit,
  getAddressSuggestions,
  getExternalSuggestions,
  clearSuggestions,
  selectAddress,
  isLoading,
}) => {
  // Requires a Router ancestor (always satisfied: rendered inside
  // FreeAssessmentPage under App.js's <Router>; tests use MemoryRouter).
  // Used to route uncovered-address picks to the national intake.
  const history = useHistory();
  const {
    address,
    getDropdownStyle,
    handleChange,
    onSuggestionFetchRequested,
    onSuggestionsClearRequested,
    searchEveryState,
    selectSuggestion,
    showAddressNotFound,
    showEscapeHatch,
    wrapperRef,
  } = useAddressAutosuggest({
    addressSuggestions,
    clearSuggestions,
    getAddressSuggestions,
    getExternalSuggestions,
    isLoading,
    suggestionsFailed,
    selectAddress,
  });

  const getSuggestionValue = (suggestion) => {
    const formatted = selectSuggestion(suggestion);
    // Addresses that only came back from the external geocoder
    // (``covered: false``) aren't in our Texas DB — route to the national
    // intake, which opportunistically checks serviceability across the 49
    // non-Texas states plus DC and captures demand when a property cannot yet
    // be served. Include the complete
    // address in the URL so the flow also survives a browser refresh.
    if (suggestion.covered === false) {
      history.push(nationalIntakeEntryUrl(suggestion));
    } else {
      onSubmit(CURRENT_STEP + 1);
    }
    return formatted;
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => (
    <AddressSuggestionsPortal
      containerProps={containerProps}
      getDropdownStyle={getDropdownStyle}
      onSearchEveryState={searchEveryState}
      showEscapeHatch={showEscapeHatch}
    >
      {children}
    </AddressSuggestionsPortal>
  );

  const inputProps = {
    onChange: handleChange,
    value: address,
    placeholder: "Address",
    type: "text",
    autoComplete: "street-address",
    "aria-label": "Enter your property address",
  };

  return (
    <>
      <h4 className="title">
        We need to know your address to get started, enter it below.
      </h4>
      <Form className="register-form" onSubmit={(e) => e.preventDefault()}>
        <AddressPillWrap ref={wrapperRef}>
          <InputGroup className="form-group-no-border">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                {isLoading ? (
                  <SmallSpinner as={Spinner} color="info" />
                ) : (
                  <PropertyIcon />
                )}
              </InputGroupText>
            </InputGroupAddon>
            <Autosuggest
              suggestions={addressSuggestions}
              renderSuggestion={renderAddressSuggestion}
              renderSuggestionsContainer={renderSuggestionsContainer}
              onSuggestionsFetchRequested={onSuggestionFetchRequested}
              onSuggestionsClearRequested={onSuggestionsClearRequested}
              getSuggestionValue={getSuggestionValue}
              inputProps={inputProps}
              renderInputComponent={(inputProps) => <Input {...inputProps} />}
              alwaysRenderSuggestions
            />
          </InputGroup>
        </AddressPillWrap>
        {showAddressNotFound && <AddressNotFoundMessage />}
        {suggestionsFailed && address.trim() && !isLoading ? (
          <AddressSearchErrorMessage
            onRetry={() => getAddressSuggestions(address)}
          />
        ) : null}
        <CountyNote>
          In Texas, we support single-family homes and condos in{" "}
          <b>Bexar, Collin, Dallas, Denton, Fort Bend, Harris, Hays, Montgomery,
            Tarrant, Travis,</b> and <b>Williamson</b> counties. For an address
          outside Texas, choose the complete address and we’ll check whether a
          national market-value report is available. Having trouble? Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </CountyNote>
      </Form>
    </>
  );
};

LookupAddressForm.propTypes = {
  addressSuggestions: PropTypes.array,
  suggestionsFailed: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  getAddressSuggestions: PropTypes.func.isRequired,
  getExternalSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  selectAddress: PropTypes.func.isRequired,
};

LookupAddressForm.defaultProps = {
  addressSuggestions: [],
  suggestionsFailed: false,
  isLoading: false,
};

export default LookupAddressForm;
